import classNames from "classnames";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Button from "components/Button";

import useRealtime from "hooks/useRealtime";

import AirQualityCard from "modules/assets/AirQualityCard";
import ComplianceTrendChart from "modules/assets/ComplianceTrendChart";
import IncidentBarChart from "modules/assets/IncidentBarChart";
import Tag from "modules/assets/Tag";
import ThermalCard from "modules/assets/ThermalCard";
import StatCard, { StatIcon, StatTone } from "modules/common/StatCard";

import {
  ComplianceLevel,
  ComplianceScore,
  HumidityCard as HumidityMetric,
  ImprovementLevel,
  IncidentStats,
  MetricCard,
  ThermalCard as ThermalMetric,
} from "entities/assets";
import { ModalType } from "entities/common";
import { AssetsApi } from "services/http";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { showModal } from "store/slices/modalSlice";
import { PAGES } from "utils/links";

import { BuildOptions, buildMetricCard } from "./co2";
import { AIR_QUALITY_CARDS, LEGEND, STAT_CARDS, THERMAL_CARDS } from "./data";
import { buildHumidityCard, buildTemperatureCard } from "./thermal";

import styles from "./style.module.scss";

const IAQ_STATUS_TOOLTIP =
  "This status represents the overall condition of the zone and is based on the most critical metric currently detected";

type IaqBucket = "alert" | "warning" | "good" | "no_data";

const IAQ_ALERT_STATUSES = ["alert", "above", "below"];

const computeIaqStatus = (statuses: (string | null | undefined)[]): IaqBucket => {
  const present = statuses.filter((status): status is string => !!status && status !== "no_data");
  if (present.some((status) => IAQ_ALERT_STATUSES.includes(status))) return "alert";
  if (present.some((status) => status === "warning")) return "warning";
  if (present.length) return "good";
  return "no_data";
};

const IAQ_STATUS_META: Record<IaqBucket, { label: string; tone: StatTone }> = {
  alert: { label: "Alert", tone: "red" },
  warning: { label: "Warning", tone: "orange" },
  good: { label: "Good", tone: "green" },
  no_data: { label: "No data available", tone: "gray" },
};

const COMPLIANCE_LEVELS: Record<ComplianceLevel, { tone: StatTone; description: string }> = {
  high: { tone: "green", description: "High compliance" },
  medium: { tone: "orange", description: "Medium compliance" },
  low: { tone: "red", description: "Low compliance" },
  insufficient: { tone: "gray", description: "Insufficient data" },
};

const IMPROVEMENT_LEVELS: Record<ImprovementLevel, { tone: StatTone; icon?: StatIcon; description: string }> = {
  up: { tone: "green", icon: "grow-up", description: "Vs previous 30 days" },
  down: { tone: "red", icon: "grow-down", description: "Vs previous 30 days" },
  flat: { tone: "gray", icon: "flat", description: "Vs previous 30 days" },
  insufficient: { tone: "gray", description: "Insufficient data" },
};

const formatImprovement = (improvement: number | null): string => {
  if (improvement === null) return "–";
  if (improvement > 0) return `+${improvement}%`;
  if (improvement < 0) return `${improvement}%`;
  return "0%";
};

const improvementTooltip = (improvement: number | null): string => {
  if (improvement === null)
    return "Insufficient data to calculate change compared to the previous 30 days. Per DM-HSD-GU119.";
  if (improvement > 0)
    return `Compliance score increased by ${improvement}% compared to the previous 30 days. Per DM-HSD-GU119.`;
  if (improvement < 0)
    return `Compliance score decreased by ${Math.abs(improvement)}% compared to the previous 30 days. Per DM-HSD-GU119.`;
  return "Compliance score did not change compared to the previous 30 days. Per DM-HSD-GU119.";
};

const avgIncidentsTone = (avg: number | null): StatTone => {
  if (avg === null) return "gray";
  if (avg === 0) return "green";
  if (avg < 4) return "orange";
  return "red";
};

const activeIncidentsTone = (active: number): StatTone => (active === 0 ? "green" : "red");

const PARAMETER_SLUG: Record<string, string> = {
  "CO₂": "co2",
  HCHO: "hcho",
  TVOC: "tvoc",
  "PM2.5": "pm25",
  PM10: "pm10",
};

const CARD_OPTIONS: Record<string, BuildOptions> = {
  "CO₂": { shortTermLabel: "Short-term - vs outdoor level" },
  HCHO: { shortTermLabel: "Short-term: 30-min average" },
  TVOC: {
    shortTermLabel: "Short-term: 30-min average",
    approximateInfo: "Approximate conversion (MOX sensor)",
  },
  "PM2.5": { shortTermLabel: "Short-term: 1-hour average", longTermLabel: "Long-term: 24-hour average" },
  PM10: { shortTermLabel: "Short-term: 8-hour average", longTermLabel: "Long-term: 24-hour average" },
};

const THERMAL_SLUG: Record<string, string> = {
  "Air temperature": "temperature",
  "Relative humidity": "humidity",
};

const AssetOverviewTab: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const asset = useAppSelector((state) => state.assets.current);
  const [compliance, setCompliance] = useState<ComplianceScore | null>(null);
  const [incidentStats, setIncidentStats] = useState<IncidentStats | null>(null);
  const [co2Card, setCo2Card] = useState<MetricCard | null>(null);
  const [hchoCard, setHchoCard] = useState<MetricCard | null>(null);
  const [tvocCard, setTvocCard] = useState<MetricCard | null>(null);
  const [pm25Card, setPm25Card] = useState<MetricCard | null>(null);
  const [pm10Card, setPm10Card] = useState<MetricCard | null>(null);
  const [temperatureCard, setTemperatureCard] = useState<ThermalMetric | null>(null);
  const [humidityCard, setHumidityCard] = useState<HumidityMetric | null>(null);

  const currentIdRef = useRef(id);
  currentIdRef.current = id;

  const isStale = useCallback((requestedId?: string) => currentIdRef.current !== requestedId, []);

  const loadComplianceScore = useCallback(() => {
    if (!id) return;

    AssetsApi.getComplianceScore(id)
      .then(({ data }) => {
        if (!isStale(id)) setCompliance(data);
      })
      .catch(() => {
        if (!isStale(id)) setCompliance(null);
      });
  }, [id, isStale]);

  const loadIncidentStats = useCallback(() => {
    if (!id) return;

    AssetsApi.getIncidentStats(id)
      .then(({ data }) => {
        if (!isStale(id)) setIncidentStats(data);
      })
      .catch(() => {
        if (!isStale(id)) setIncidentStats(null);
      });
  }, [id, isStale]);

  const loadMetricCards = useCallback(() => {
    if (!id) return;

    const setters: [string, (value: MetricCard | null) => void][] = [
      ["co2", setCo2Card],
      ["hcho", setHchoCard],
      ["tvoc", setTvocCard],
      ["pm25", setPm25Card],
      ["pm10", setPm10Card],
    ];

    setters.forEach(([metric, setter]) => {
      AssetsApi.getMetricCard(id, metric)
        .then(({ data }) => {
          if (!isStale(id)) setter(data);
        })
        .catch(() => {
          if (!isStale(id)) setter(null);
        });
    });
  }, [id, isStale]);

  const loadThermalCards = useCallback(() => {
    if (!id) return;

    AssetsApi.getThermalCard(id, "temperature")
      .then(({ data }) => {
        if (!isStale(id)) setTemperatureCard(data);
      })
      .catch(() => {
        if (!isStale(id)) setTemperatureCard(null);
      });

    AssetsApi.getHumidityCard(id)
      .then(({ data }) => {
        if (!isStale(id)) setHumidityCard(data);
      })
      .catch(() => {
        if (!isStale(id)) setHumidityCard(null);
      });
  }, [id, isStale]);

  useEffect(() => {
    loadComplianceScore();
  }, [loadComplianceScore]);

  useEffect(() => {
    loadIncidentStats();
  }, [loadIncidentStats]);

  useEffect(() => {
    loadMetricCards();
  }, [loadMetricCards]);

  useEffect(() => {
    loadThermalCards();
  }, [loadThermalCards]);

  const reload = useCallback(() => {
    loadComplianceScore();
    loadIncidentStats();
    loadMetricCards();
    loadThermalCards();
  }, [loadComplianceScore, loadIncidentStats, loadMetricCards, loadThermalCards]);

  useRealtime(["telemetry", "incidents", "compliance"], reload, { assetId: Number(id) });

  const statCards = STAT_CARDS.map((card, index) => {
    if (index === 0) {
      const meta = COMPLIANCE_LEVELS[compliance?.level ?? "insufficient"];
      const value = !compliance || compliance.value === null ? "–" : `${compliance.value}%`;
      return { ...card, value, description: meta.description, tone: meta.tone };
    }

    if (index === 1) {
      const meta = IMPROVEMENT_LEVELS[compliance?.improvement_level ?? "insufficient"];
      const improvement = compliance?.improvement ?? null;
      return {
        ...card,
        value: formatImprovement(improvement),
        description: meta.description,
        tone: meta.tone,
        icon: meta.icon,
        tooltip: improvementTooltip(improvement),
      };
    }

    if (index === 2) {
      const avg = incidentStats?.avg_per_month ?? null;
      return {
        ...card,
        value: avg === null ? "–" : `${avg}`,
        description: avg === null ? "Insufficient data" : "Last 3 months",
        tone: avgIncidentsTone(avg),
      };
    }

    if (index === 3) {
      const activeCount = incidentStats?.active ?? 0;
      return {
        ...card,
        value: `${activeCount}`,
        tone: activeIncidentsTone(activeCount),
      };
    }

    return card;
  });

  const CARD_SOURCES: Record<string, MetricCard | null> = {
    "CO₂": co2Card,
    HCHO: hchoCard,
    TVOC: tvocCard,
    "PM2.5": pm25Card,
    PM10: pm10Card,
  };

  const handleGenerateReport = () => {
    if (!asset) return;
    dispatch(
      showModal({
        modalType: ModalType.GENERATE_REPORT,
        modalProps: {
          level: "asset",
          ...(asset.company_id != null
            ? { company: { value: String(asset.company_id), label: asset.company_name ?? "" } }
            : {}),
          portfolio: { value: String(asset.portfolio_id), label: asset.portfolio_name ?? "" },
          asset: { value: String(asset.id), label: asset.name },
        },
      }),
    );
  };

  const iaqStatus = computeIaqStatus([
    co2Card?.overall_status,
    hchoCard?.overall_status,
    tvocCard?.overall_status,
    pm25Card?.overall_status,
    pm10Card?.overall_status,
    temperatureCard?.status,
    humidityCard?.status,
  ]);
  const iaqStatusMeta = IAQ_STATUS_META[iaqStatus];

  return (
    <div className={styles.overview}>
      <div className={styles.stats}>
        {statCards.map((card) => (
          <StatCard key={card.title} data={card} />
        ))}
      </div>

      <div className={styles.dashboard}>
        <div className={styles.dashboardHead}>
          <div className={styles.dashboardHeading}>
            <h2 className={styles.dashboardTitle}>Indoor air quality monitoring dashboard</h2>
            <p className={styles.dashboardSubtitle}>Standard: DM-HSD-GU119, for existing buildings</p>
          </div>

          <div className={styles.dashboardActions}>
            <Button
              variant="secondary"
              className={styles.dashboardButton}
              onClick={() => id && navigate(PAGES.CHARTS.replace(":id", String(id)))}
            >
              Historical trends
            </Button>
            <Button variant="primary" className={styles.dashboardButton} onClick={handleGenerateReport}>
              Generate report
            </Button>
          </div>
        </div>

        <div className={classNames(styles.banner, styles[`banner-${iaqStatusMeta.tone}`])}>
          <span className={styles.status} tabIndex={0}>
            <span className={styles.bannerText}>Overall IAQ Status: {iaqStatusMeta.label}</span>
            <span className={styles.tooltip}>{IAQ_STATUS_TOOLTIP}</span>
          </span>
        </div>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Air quality parameters</h3>

          <div className={styles.legend}>
            {LEGEND.map((item) => (
              <div key={item.label} className={styles.legendItem}>
                <span className={styles.legendLabel}>{item.label}</span>
                <Tag tone={item.tag.tone}>{item.tag.label}</Tag>
              </div>
            ))}
          </div>

          <div className={styles.parameters}>
            {AIR_QUALITY_CARDS.map((card) => {
              const slug = PARAMETER_SLUG[card.formula];
              const options = CARD_OPTIONS[card.formula];
              const source = CARD_SOURCES[card.formula];
              const data = options && source ? buildMetricCard(card, source, options) : card;
              const breakdown =
                id && slug ? PAGES.ASSET_PARAMETER_BREAKDOWN.replace(":id", id).replace(":parameter", slug) : undefined;
              const to = options ? (source && source.sensor_count > 1 ? breakdown : undefined) : breakdown;
              return <AirQualityCard key={card.formula} data={data} to={to} className={styles.parameterCard} />;
            })}
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Thermal comfort</h3>

          <div className={styles.thermal}>
            {THERMAL_CARDS.map((card) => {
              const slug = THERMAL_SLUG[card.title];
              const breakdown =
                id && slug ? PAGES.ASSET_PARAMETER_BREAKDOWN.replace(":id", id).replace(":parameter", slug) : undefined;

              let data = card;
              let sensorCount: number | null = null;
              if (card.title === "Air temperature" && temperatureCard) {
                data = buildTemperatureCard(card, temperatureCard);
                sensorCount = temperatureCard.sensor_count;
              } else if (card.title === "Relative humidity" && humidityCard) {
                data = buildHumidityCard(card, humidityCard);
                sensorCount = humidityCard.sensor_count;
              }

              const to = sensorCount === null ? breakdown : sensorCount > 1 ? breakdown : undefined;
              return <ThermalCard key={card.title} data={data} to={to} className={styles.thermalCard} />;
            })}
          </div>
        </section>

        <ComplianceTrendChart id={id} />

        <IncidentBarChart id={id} />
      </div>
    </div>
  );
};

export default AssetOverviewTab;
